/**
 * Local fetch-based service for the XEPPT Order Refund endpoints.
 * Does NOT use the xeppt-sdk — these endpoints extend the Orders API
 * with refund-specific quote and execution operations.
 *
 * AUTH: Bearer access token from localStorage 'client-metadata',
 * same pattern as connect_service.ts and payout_service.ts.
 *
 * API CONTRACT (backend must implement):
 *   GET  /orders/{orderId}/refund-quote?amount={amount}
 *   → { available_to_refund, estimated_fee, total, points, limits: { daily, weekly, monthly } }
 *
 *   POST /orders/{orderId}/refund
 *   body { reason, amount?, idempotency_key? }
 *   → { id, amount, fee, total, points, currency, status, created_at,
 *       fully_refunded, refunded_amount, remaining_amount }
 *   Note: the POST response returns amounts as decimal strings.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

function authHeaders(): Record<string, string> {
    try {
        const raw = localStorage.getItem('client-metadata');
        if (!raw) return {};
        const meta = JSON.parse(raw) as { accessToken?: string };
        return meta?.accessToken ? { Authorization: `Bearer ${meta.accessToken}` } : {};
    } catch {
        return {};
    }
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RefundLimitPeriod {
    used: number;
    cap: number;
}

export interface RefundLimits {
    daily: RefundLimitPeriod;
    weekly: RefundLimitPeriod;
    monthly: RefundLimitPeriod;
}

export interface RefundQuote {
    available_to_refund: number;
    estimated_fee: number;
    total: number;
    points: number;
    limits: RefundLimits;
}

export interface RefundRequest {
    reason: string;
    amount?: number;
    idempotency_key?: string;
}

export interface RefundResult {
    id: string;
    /** Decimal string — POST endpoint returns amounts as strings. */
    amount: string;
    fee: string;
    total: string;
    points: number;
    currency: string;
    status: string;
    created_at: string;
    fully_refunded: boolean;
    refunded_amount: string;
    remaining_amount: string;
}

// ─── Error class ─────────────────────────────────────────────────────────────

export class RefundApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
        options?: ErrorOptions
    ) {
        super(message, options);
        this.name = 'RefundApiError';
    }
}

async function throwOnError(response: Response): Promise<never> {
    let code: string | undefined;
    let message: string | undefined;
    try {
        const errBody = (await response.json()) as { code?: string; message?: string };
        code = typeof errBody?.code === 'string' ? errBody.code : undefined;
        message = typeof errBody?.message === 'string' ? errBody.message : undefined;
    } catch {
        // ignore JSON parse failures on error bodies
    }
    throw new RefundApiError(
        message ?? `HTTP ${response.status}`,
        response.status,
        code
    );
}

async function parseResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        await throwOnError(response);
    }
    return response.json() as Promise<T>;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const apiRefundService = {
    /**
     * GET /orders/{orderId}/refund-quote?amount={amount}
     * Returns fee estimate, total, points to earn, and current refund limits.
     */
    async getRefundQuote(orderId: string, amount: number): Promise<RefundQuote> {
        const qs = new URLSearchParams({ amount: String(amount) });
        const response = await fetch(
            `${BACKEND_URL}/orders/${orderId}/refund-quote?${qs.toString()}`,
            { headers: authHeaders() }
        );
        return parseResponse<RefundQuote>(response);
    },

    /**
     * POST /orders/{orderId}/refund
     * Executes the refund. Supply idempotency_key to prevent duplicate submissions
     * on retry (network errors, double-click, etc.).
     */
    async refundOrder(orderId: string, body: RefundRequest): Promise<RefundResult> {
        // The backend `amount` field is a decimal STRING ("9.99"), not a number —
        // sending a JSON number makes Go's unmarshal into *string fail (400). Omit
        // amount entirely for a full-remaining refund.
        const payload: { reason: string; amount?: string; idempotency_key?: string } = {
            reason: body.reason
        };
        if (body.amount != null) payload.amount = body.amount.toFixed(2);
        if (body.idempotency_key) payload.idempotency_key = body.idempotency_key;
        const response = await fetch(`${BACKEND_URL}/orders/${orderId}/refund`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(payload)
        });
        return parseResponse<RefundResult>(response);
    }
};
